import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCampuses } from "../../contexts/CampusContext";
import { useLeaders } from '../../contexts/LeaderContext';
import PageHeader from '../common/PageHeader';

const Campus = () => {
  const {campuses} = useCampuses();
  const {leaders} = useLeaders();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCampus, setActiveCampus] = useState(null);
  const [enhancedCampuses, setEnhancedCampuses] = useState([]);
  const PlaceholderImage = "https://firebasestorage.googleapis.com/v0/b/myportfolio-fecaa.appspot.com/o/Excelent%20love%20ministries%20personels%2Fplaceholder.jpg?alt=media&token=3bff1d6b-5432-4ac4-bd29-30382d042fb6";
useEffect(() => {
  if (campuses && leaders) {

    const updatedCampuses = campuses.map(campus => {
      // Find ALL leaders associated with this campus
      const campusLeaders = leaders.filter(leader => 
        leader.campusId === campus.id && 
        leader.leaderType.includes("campus")
      );

      // Categorize leaders
      const primaryPastor = campusLeaders.find(leader => 
        leader.role?.[0] === "Regional Pastor"
      );

      const supportingPastors = campusLeaders.filter(leader => 
        leader.role?.includes("Regional Pastor") 
      );
   
      const assistantLeaders = campusLeaders.filter(leader => 
        leader.role?.includes("Assistant Leader") && 
        !leader.role?.includes("Regional Pastor")
      );

      // Combine all leaders in display order
      const allLeaders = [
        ...(primaryPastor ? [primaryPastor] : []),
        ...supportingPastors,
        ...assistantLeaders
      ];

      return {
        ...campus,
        leaders: allLeaders,
        hasPrimaryPastor: !!primaryPastor,
        // Backward compatibility
        pastor: primaryPastor?.name || 
               supportingPastors[0]?.name || 
               assistantLeaders[0]?.name || 
               "Not Assigned",
        pastor_image: primaryPastor?.profile_image || 
                    supportingPastors[0]?.profile_image || 
                    assistantLeaders[0]?.profile_image || 
                    PlaceholderImage,
        pastor_role: primaryPastor ? "Regional Pastor" :
                    supportingPastors[0] ? "Supporting Pastor" :
                    assistantLeaders[0] ? "Assistant Leader" :
                    "Not Assigned"
      };
    });
    // Sort campuses by established date
    updatedCampuses.sort((a, b) => new Date(b.established) - new Date(a.established));
    updatedCampuses.reverse();
    setEnhancedCampuses(updatedCampuses);
    setLoading(false);
  }
}, [campuses, leaders]);

  const handleCampusSelect = (campusId) => {
    setActiveCampus(activeCampus === campusId ? null : campusId);
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <PageHeader 
          title="Our Campuses" 
          subtitle="Find a location near you to join our community"
          imageUrl="https://i.pinimg.com/736x/5b/6b/bf/5b6bbf21c1f8b8c9fb4aa260273f5f16.jpg"
          imageAlt="Church Campuses"
        />
        <div className="container mx-auto px-4 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200"></div>
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary absolute top-0 left-0"></div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <PageHeader 
          title="Our Campuses" 
          subtitle="Find a location near you to join our community"
          imageUrl="https://i.pinimg.com/736x/5b/6b/bf/5b6bbf21c1f8b8c9fb4aa260273f5f16.jpg"
          imageAlt="Church Campuses"
        />
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg shadow-sm">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-red-800">Something went wrong</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <PageHeader 
        title="Our Campuses" 
        subtitle="Find a location near you to join our community"
        imageUrl="https://i.pinimg.com/736x/5b/6b/bf/5b6bbf21c1f8b8c9fb4aa260273f5f16.jpg"
        imageAlt="Church Campuses"
      />

      <div className="container mx-auto px-4 py-16">
        {/* Campus Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {enhancedCampuses.map((campus) => (
            <div key={campus.id} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-200/50">
              {/* Map Section */}
              <div className="relative h-64 overflow-hidden">
                <APIProvider apiKey="AIzaSyADZ4xvTeIRlQeIDceZra_mZ8so4C42azw">
                                      <Map
                                        defaultCenter={{
                                          lat: campus.latitude,
                                          lng: campus.longitude,
                                        }}
                                        defaultZoom={14}
                                        gestureHandling={"greedy"}
                                        disableDefaultUI={true}
                                        className="w-full h-full rounded-lg"
                                      >
                                        <Marker position={{
                                          lat: campus.latitude,
                                          lng: campus.longitude,
                                        }} />
                                      </Map>
                                    </APIProvider>
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  <span className="text-sm font-semibold text-slate-700">Est. {campus.established}</span>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-8">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-1 group-hover:text-primary transition-colors duration-300">
                      {campus.name}
                    </h3>
                    <div className="flex items-center text-slate-600">
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm">{campus.address}</span>
                    </div>
                  </div>
                </div>

                
                {/* Pastor Card */}
<div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-6 border border-primary-light">
  {/* Primary Pastor */}
  {campus.leaders?.some(l => l.role?.[0] === "Regional Pastor") ? (
    <div className="flex items-center space-x-4 mb-3">
      <div className="relative">
        <img
          src={campus.leaders.find(l => l.role?.[0] === "Regional Pastor")?.profile_image || PlaceholderImage}
          alt="Primary Regional Pastor"
          className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-md"
        />
        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
      </div>
      <div>
        <p className="font-semibold text-slate-900">
          {campus.leaders.find(l => l.role?.[0] === "Regional Pastor")?.name}
        </p>
        <p className="text-sm text-slate-600">Regional Pastor</p>
      </div>
    </div>
  ) : (
    <div className="flex items-center space-x-4 mb-3">
      <div className="relative">
        <img
          src={campus.leaders.find(l => l.role?.[0] === "Regional Pastor")?.profile_image || PlaceholderImage}
          alt="Primary Regional Pastor"
          className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-md"
        />
        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
      </div>
      <div>
        <p className="font-semibold text-slate-900">
          Not Assigned
        </p>
      </div>
    </div>)
  }

  {/* Supporting Leadership */}
  {campus.leaders?.filter(l => 
    (l.role?.includes("Regional Pastor") && l.role?.[0] !== "Regional Pastor") || 
    l.role?.includes("Assistant Leader")
  ).length > 0 && (
    <div className="space-y-3 mt-3 pl-16 border-t border-slate-200 pt-3">
      <p className="text-xs font-medium text-slate-500">
        {campus.hasPrimaryPastor ? "Supported by:" : "Leadership Team:"}
      </p>
      <div className="flex flex-wrap gap-3">
        {campus.leaders
          .filter(l => 
            (l.role?.includes("Regional Pastor") && l.role?.[0] !== "Regional Pastor") || 
            l.role?.includes("Assistant Leader")
          )
          .map((leader, index) => (
            <div key={index} className="flex items-center space-x-2">
              <img
                src={leader.profile_image || PlaceholderImage}
                alt={leader.name}
                className="w-8 h-8 rounded-full object-cover ring-1 ring-white shadow-sm"
              />
              <div>
                <span className="text-xs font-medium text-slate-700 block">
                  {leader.name}
                </span>
                <span className="text-xs text-slate-500 block">
                  {leader.role?.includes("Regional Pastor") ? "Supporting Pastor" : "Assistant Leader"}
                </span>
              </div>
            </div>
          ))}
      </div>
    </div>
  )}

  {/* No Leaders Case 
  {!campus.leaders?.length && (
    <div className="flex items-center space-x-4">
      <div className="relative">
        <img
          src={PlaceholderImage}
          alt="Not Assigned"
          className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-md"
        />
      </div>
      <div>
        <p className="font-semibold text-slate-900">Not Assigned</p>
        <p className="text-sm text-slate-600">Contact headquarters for information</p>
      </div>
    </div>
  )}*/}
</div>

                {/* Contact & Service Times Grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Contact Info */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 flex items-center">
                      <div className="w-2 h-2 bg-primary rounded-full mr-2"></div>
                      Contact
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-slate-600">
                        <svg className="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {campus.leaders[0]?.phone || "Not Available"}
                      </div>
                      <div className="flex items-center text-slate-600">
                        <svg className="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {campus.contactEmail}
                      </div>
                    </div>
                  </div>

                  {/* Service Times */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 flex items-center">
                      <div className="w-2 h-2 bg-primary rounded-full mr-2"></div>
                      Service Times
                    </h4>
                    <div className="space-y-2">
                      {campus.services.map((service, index) => (
                        <div key={index} className="text-sm text-slate-600">
                          {service.type} - {service.day}, {service.time}
                        </div>
                      ))}
                      {campus.services.length > 3 && (
                        <button
                          onClick={() => handleCampusSelect(campus.id)}
                          className="text-sm text-primary hover:text-primary-dark font-medium"
                        >
                          +{campus.services.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Features Tags 
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {campus.features.slice(0, 4).map((feature, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">
                        {feature}
                      </span>
                    ))}
                    {campus.features.length > 4 && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-light text-primary-dark">
                        +{campus.features.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
                */}
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button 
                    onClick={() => handleCampusSelect(campus.id)}
                    className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary-dark transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {activeCampus === campus.id ? "Hide Details" : "View Details"}
                  </button>
                  
                </div>

                {/* Expanded Details */}
                {activeCampus === campus.id && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* All Service Times */}
                      <div className="space-y-3">
                        <h5 className="font-semibold text-slate-900 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          All Service Times
                        </h5>
                        <div className="space-y-2">
                          {campus.services.map((service, index) => (
                            <div key={index} className="flex items-center p-2 bg-slate-50 rounded-lg">
                              <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                              <span className="text-sm text-slate-700">{service.type} - {service.day}, {service.time}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* All Features 
                      <div className="space-y-3">
                        <h5 className="font-semibold text-slate-900 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          Campus Features
                        </h5>
                        <div className="grid grid-cols-1 gap-2">
                          {campus.features.map((feature, index) => (
                            <div key={index} className="flex items-center p-2 bg-slate-50 rounded-lg">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                              <span className="text-sm text-slate-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      */}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-primary via-purple-600 to-indigo-600 rounded-3xl shadow-2xl">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative px-8 py-16 text-center">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Visit Us?
              </h3>
              <p className="text-xl text-white mb-8 leading-relaxed">
                Join our growing community of believers. Find your spiritual home at one of our welcoming campuses.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/contact" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-2xl hover:bg-white/20 transition-all duration-200 font-semibold"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Get In Touch
                </Link>
              </div>
            </div>
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
        </div>
      </div>
    </main>
  );
};

export default Campus;